import { useCallback, useEffect, useState } from "react";

import { Button } from "../../../components/Button";
import { Drawer } from "../../../components/Drawer";
import { Input } from "../../../components/Input";
import { Label } from "../../../components/Label";
import { Radio } from "../../../components/Radio";

import { OlgaEnvironmentsAPI } from "../../../../providers/OlgaEnvironmentsAPI";
import { StandaloneCouponsAPI } from "../../../../providers/StandaloneCouponsAPI";
import styles from "../../../../styles/beneficios/CreateFolderDrawer.module.scss";

import debounce from "lodash.debounce";

const steps = {
  REMOVE_FOLDER: "REMOVE_FOLDER",
  MOVE_BENEFITS: "MOVE_BENEFITS",
};

export default function RemoveFolderDrawer({
  visible,
  folder,
  onClose,
  onBack,
  onLoadCoupons,
  onLoadFolders,
}) {
  const [step, setStep] = useState(steps.REMOVE_FOLDER);
  const [folders, setFolders] = useState([]);
  const [destinationFolder, setDestinationFolder] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const [archiving, setArchiving] = useState(false);
  const [moving, setMoving] = useState(false);
  const [unlinking, setUnlinking] = useState(false);

  useEffect(() => {
    if (visible) loadFolders();
  }, [visible]);

  const loadFolders = async () => {
    const folders = await getFolders();
    setFolders(folders);
  };

  const getFolders = async () => {
    const environmentConfigurations =
      await OlgaEnvironmentsAPI.getConfigurations();
    return environmentConfigurations.getData({})?.coupon_folders || [];
  };

  const archiveFolder = async () => {
    setArchiving(true);

    const deleteResponse = await StandaloneCouponsAPI.deleteFolder(folder.id);
    if (deleteResponse.getErrors([]).length > 0) {
      alert({ text: deleteResponse.getErrors()[0] });
      return setArchiving(false);
    }

    setArchiving(false);
    onBack();
    onLoadFolders();
    setStep(steps.REMOVE_FOLDER);
  };

  const moveFolder = async () => {
    setMoving(true);

    const moveResponse = await StandaloneCouponsAPI.moveFolder(
      folder.id,
      destinationFolder.id
    );
    if (moveResponse.getErrors([]).length > 0) {
      alert({ text: moveResponse.getErrors()[0] });
      return setMoving(false);
    }

    setMoving(false);
    onBack();
    onLoadFolders();
    loadFolders();
    setStep(steps.REMOVE_FOLDER);
  };

  const unlinkFolder = async () => {
    setUnlinking(true);
    const moveResponse = await StandaloneCouponsAPI.moveFolder(folder.id, null);

    if (moveResponse.getErrors([]).length > 0) {
      alert({ text: moveResponse.getErrors()[0] });
      return setUnlinking(false);
    }

    const currentFolders = await getFolders();
    const updatedFolders = currentFolders.filter(
      (existingFolder) => existingFolder.id != folder.id
    );

    await OlgaEnvironmentsAPI.putConfigurations({
      coupon_folders: updatedFolders,
    });

    setUnlinking(false);
    onBack();
    onLoadCoupons();
    onLoadFolders();
    loadFolders();
    setStep(steps.REMOVE_FOLDER);
  };

  const filterFolder = () => {
    if (!debouncedSearchTerm) {
      return folders;
    }

    return folders.filter((folder) => {
      const name = folder?.name.toLowerCase();
      const filterName = debouncedSearchTerm.toLowerCase();
      return name.includes(filterName);
    });
  };

  const prevStep = () => {
    if (step === steps.MOVE_BENEFITS) {
      return setStep(steps.REMOVE_FOLDER);
    } else {
      setStep(steps.REMOVE_FOLDER);
      onBack();
    }
  };

  const titles = {
    [steps.REMOVE_FOLDER]: "Excluir pasta",
    [steps.MOVE_BENEFITS]: "Mover benefícios",
  };

  const debounceSearchTerm = useCallback(
    debounce((searchTerm) => {
      setDebouncedSearchTerm(searchTerm);
    }, 500),
    []
  );

  const filteredFolders = filterFolder(folders);

  return (
    <Drawer
      visible={visible}
      onClose={() => {
        setStep(steps.REMOVE_FOLDER);
        onClose();
      }}
      title={titles[step]}
      subtitle={`"${folder?.name}"`}
      showBackButton
      backButton={prevStep}
      withoutTransition
    >
      {step === steps.REMOVE_FOLDER ? (
        <>
          <Label>Você gostaria de arquivar os benefícios ou manter?</Label>

          <Button
            className="mb-15 mt-15"
            onClick={() => setStep(steps.MOVE_BENEFITS)}
            disabled={archiving}
          >
            Desejo manter os benefícios
          </Button>
          <Button
            onClick={archiveFolder}
            disabled={archiving}
            loading={archiving}
          >
            Arquivar esta pasta
          </Button>
        </>
      ) : null}

      {step === steps.MOVE_BENEFITS ? (
        <>
          <Label>
            Para que pasta você gostaria de mover os benefícios de "
            {folder?.name}"?
          </Label>

          <Input
            search
            value={searchTerm}
            records={filteredFolders.length}
            placeholder="Buscar por pasta..."
            prefix
            prefixWidth={20}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              debounceSearchTerm(e.target.value);
            }}
          />

          <div className={styles.createdFolders}>
            {filteredFolders.map((folder) => (
              <div
                className={styles.folder}
                key={folder.id}
                onClick={() => setDestinationFolder(folder)}
              >
                <Radio
                  family="folder"
                  value={folder.id}
                  checked={destinationFolder ? destinationFolder.id : null}
                >
                  {folder?.name}
                </Radio>
              </div>
            ))}
          </div>

          <div className={styles.footer}>
            {destinationFolder && (
              <Button
                className="w-100 mb-15"
                onClick={moveFolder}
                design="primary"
                loading={moving}
                disabled={moving || unlinking}
              >
                Salvar
              </Button>
            )}

            <Button
              className="w-100"
              onClick={unlinkFolder}
              loading={unlinking}
              disabled={moving || unlinking}
            >
              Não quero agrupar benefício
            </Button>
          </div>
        </>
      ) : null}
    </Drawer>
  );
}
